import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CheckSquare,
  ChevronDown,
  Loader2,
  Plus,
  Sparkles,
  X,
} from "lucide-react";
import { useState } from "react";

import {
  decisions as decisionsApi,
  type ApiDecisionAction,
  type ApiDecisionActionStatus,
  type ApiSuggestedAction,
} from "@/lib/api";

const STATUS_OPTIONS: { value: ApiDecisionActionStatus; label: string }[] = [
  { value: "open", label: "Open" },
  { value: "in_progress", label: "In progress" },
  { value: "completed", label: "Completed" },
  { value: "blocked", label: "Blocked" },
  { value: "cancelled", label: "Cancelled" },
];

const STATUS_CLASSES: Record<ApiDecisionActionStatus, string> = {
  open: "border-border text-muted-foreground",
  in_progress: "border-primary/40 text-primary",
  completed: "border-risk-low/40 text-risk-low",
  blocked: "border-risk-high/40 text-risk-high",
  cancelled: "border-border text-muted-foreground line-through",
};

export function ActionTracker({
  workspaceId,
  decisionId,
  pendingSuggestions,
  onClearSuggestions,
  onSuggestionsSaved,
}: {
  workspaceId: string;
  decisionId: string;
  pendingSuggestions: ApiSuggestedAction[];
  onClearSuggestions: () => void;
  onSuggestionsSaved: () => void;
}) {
  const qc = useQueryClient();
  const [addingBlank, setAddingBlank] = useState(false);

  const listQuery = useQuery({
    queryKey: ["decision-actions", workspaceId, decisionId],
    queryFn: () => decisionsApi.actions.list(workspaceId, decisionId),
  });

  const invalidate = () =>
    qc.invalidateQueries({
      queryKey: ["decision-actions", workspaceId, decisionId],
    });

  const actions = listQuery.data ?? [];

  return (
    <div className="panel p-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="label-eyebrow mb-0.5">Action Tracker</div>
          <div className="text-[13px] font-semibold">Follow-up work</div>
        </div>
        <button
          onClick={() => setAddingBlank(true)}
          className="inline-flex items-center gap-1 rounded-md border border-border bg-surface px-2.5 py-1 text-[11.5px] font-semibold hover:bg-surface-elevated"
        >
          <Plus className="h-3.5 w-3.5" /> Add action
        </button>
      </div>

      {pendingSuggestions.length > 0 && (
        <SuggestionPanel
          workspaceId={workspaceId}
          decisionId={decisionId}
          suggestions={pendingSuggestions}
          onClear={onClearSuggestions}
          onSaved={() => {
            invalidate();
            onSuggestionsSaved();
          }}
        />
      )}

      {addingBlank && (
        <NewActionForm
          workspaceId={workspaceId}
          decisionId={decisionId}
          onDone={() => {
            setAddingBlank(false);
            invalidate();
          }}
          onCancel={() => setAddingBlank(false)}
        />
      )}

      {listQuery.isLoading ? (
        <div className="text-[11.5px] text-muted-foreground py-4 text-center">
          Loading…
        </div>
      ) : actions.length === 0 && pendingSuggestions.length === 0 && !addingBlank ? (
        <div className="text-[11.5px] text-muted-foreground py-4 text-center">
          <CheckSquare className="h-4 w-4 mx-auto mb-1.5 text-muted-foreground/60" />
          No actions yet. When you accept a decision, Horizon proposes follow-ups.
        </div>
      ) : (
        <div className="space-y-2">
          {actions.map((a) => (
            <ActionRow
              key={a.id}
              action={a}
              workspaceId={workspaceId}
              onChange={invalidate}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- Suggestions confirmation ----------

function SuggestionPanel({
  workspaceId,
  decisionId,
  suggestions,
  onClear,
  onSaved,
}: {
  workspaceId: string;
  decisionId: string;
  suggestions: ApiSuggestedAction[];
  onClear: () => void;
  onSaved: () => void;
}) {
  const [edited, setEdited] = useState<ApiSuggestedAction[]>(suggestions);
  const [discarded, setDiscarded] = useState<Set<number>>(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const remaining = edited
    .map((a, i) => ({ a, i }))
    .filter(({ i }) => !discarded.has(i));

  const saveAll = async () => {
    setSaving(true);
    setError(null);
    try {
      for (const { a } of remaining) {
        await decisionsApi.actions.create(workspaceId, decisionId, {
          title: a.title,
          description: a.description ?? null,
        });
      }
      onSaved();
      onClear();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save actions");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-md border border-primary/30 bg-primary/5 p-3 mb-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-[11.5px] font-semibold text-primary">
          <Sparkles className="h-3.5 w-3.5" />
          Horizon suggested {suggestions.length} follow-up
          {suggestions.length === 1 ? "" : "s"}
        </div>
        <button
          onClick={onClear}
          className="text-[11px] text-muted-foreground hover:text-foreground"
        >
          Dismiss all
        </button>
      </div>
      <div className="space-y-2">
        {edited.map((a, i) => {
          if (discarded.has(i)) return null;
          return (
            <div key={i} className="rounded-md border border-border bg-surface p-2">
              <input
                value={a.title}
                onChange={(e) =>
                  setEdited((prev) =>
                    prev.map((x, xi) =>
                      xi === i ? { ...x, title: e.target.value } : x,
                    ),
                  )
                }
                className="w-full mb-1 rounded-sm bg-transparent px-1 py-0.5 text-[13px] font-semibold focus:outline-none focus:ring-1 focus:ring-primary"
              />
              <textarea
                value={a.description ?? ""}
                onChange={(e) =>
                  setEdited((prev) =>
                    prev.map((x, xi) =>
                      xi === i ? { ...x, description: e.target.value } : x,
                    ),
                  )
                }
                rows={2}
                placeholder="Optional description"
                className="w-full rounded-sm bg-transparent px-1 py-0.5 text-[12px] text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              />
              <div className="flex items-center justify-between mt-1">
                {a.suggested_owner_role && (
                  <span className="text-[10px] text-muted-foreground uppercase tracking-wider">
                    Suggested owner role: {a.suggested_owner_role}
                  </span>
                )}
                <button
                  onClick={() =>
                    setDiscarded((prev) => new Set(prev).add(i))
                  }
                  className="ml-auto text-[11px] text-muted-foreground hover:text-risk-high inline-flex items-center gap-0.5"
                >
                  <X className="h-3 w-3" /> Discard
                </button>
              </div>
            </div>
          );
        })}
      </div>
      {error && (
        <div className="mt-2 text-[11.5px] text-risk-high">{error}</div>
      )}
      <div className="flex items-center justify-end gap-2 mt-3">
        <button
          onClick={saveAll}
          disabled={saving || remaining.length === 0}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground px-3 py-1.5 text-[12px] font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary/90"
        >
          {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          Save {remaining.length} action{remaining.length === 1 ? "" : "s"}
        </button>
      </div>
    </div>
  );
}

// ---------- Blank new-action form ----------

function NewActionForm({
  workspaceId,
  decisionId,
  onDone,
  onCancel,
}: {
  workspaceId: string;
  decisionId: string;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: () =>
      decisionsApi.actions.create(workspaceId, decisionId, {
        title: title.trim(),
        description: description.trim() || null,
      }),
    onSuccess: () => {
      onDone();
    },
    onError: (e) => setError(e instanceof Error ? e.message : "Failed"),
  });
  return (
    <div className="rounded-md border border-border bg-surface p-2 mb-3">
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Action title"
        className="w-full mb-1 rounded-sm bg-transparent px-1 py-0.5 text-[13px] font-semibold focus:outline-none focus:ring-1 focus:ring-primary"
      />
      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        rows={2}
        placeholder="Optional description"
        className="w-full rounded-sm bg-transparent px-1 py-0.5 text-[12px] text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
      />
      {error && (
        <div className="text-[11.5px] text-risk-high mt-1">{error}</div>
      )}
      <div className="flex items-center justify-end gap-2 mt-1">
        <button
          onClick={onCancel}
          className="text-[11px] text-muted-foreground hover:text-foreground"
        >
          Cancel
        </button>
        <button
          onClick={() => create.mutate()}
          disabled={create.isPending || !title.trim()}
          className="inline-flex items-center gap-1 rounded-md bg-primary text-primary-foreground px-2.5 py-1 text-[11.5px] font-semibold disabled:opacity-50 hover:bg-primary/90"
        >
          {create.isPending && <Loader2 className="h-3 w-3 animate-spin" />}
          Add
        </button>
      </div>
    </div>
  );
}

// ---------- Persisted action row ----------

function ActionRow({
  action,
  workspaceId,
  onChange,
}: {
  action: ApiDecisionAction;
  workspaceId: string;
  onChange: () => void;
}) {
  const [outcome, setOutcome] = useState(action.outcome ?? "");
  const [editingOutcome, setEditingOutcome] = useState(false);
  const patch = useMutation({
    mutationFn: (payload: {
      status?: ApiDecisionActionStatus;
      outcome?: string | null;
    }) => decisionsApi.actions.patch(workspaceId, action.decision_id, action.id, payload),
    onSuccess: () => onChange(),
  });
  return (
    <div className="rounded-md border border-border bg-surface p-2.5">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div
            className={`text-[13px] font-semibold ${
              action.status === "cancelled" || action.status === "completed"
                ? "text-muted-foreground"
                : "text-foreground"
            }`}
          >
            {action.title}
          </div>
          {action.description && (
            <p className="text-[12px] text-muted-foreground mt-0.5">
              {action.description}
            </p>
          )}
        </div>
        <StatusSelect
          value={action.status}
          onChange={(status) => patch.mutate({ status })}
        />
      </div>
      <div className="mt-2 flex items-center gap-2">
        {editingOutcome ? (
          <>
            <input
              autoFocus
              value={outcome}
              onChange={(e) => setOutcome(e.target.value)}
              placeholder="Outcome"
              className="flex-1 rounded-sm border border-border bg-background px-2 py-1 text-[11.5px] focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <button
              onClick={() => {
                patch.mutate({ outcome });
                setEditingOutcome(false);
              }}
              className="text-[11px] font-semibold text-primary hover:underline"
            >
              Save
            </button>
            <button
              onClick={() => {
                setOutcome(action.outcome ?? "");
                setEditingOutcome(false);
              }}
              className="text-[11px] text-muted-foreground hover:text-foreground"
            >
              Cancel
            </button>
          </>
        ) : (
          <>
            <span className="text-[11px] text-muted-foreground flex-1 truncate">
              {action.outcome || "No outcome recorded"}
            </span>
            <button
              onClick={() => setEditingOutcome(true)}
              className="text-[11px] text-muted-foreground hover:text-foreground"
            >
              Edit outcome
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function StatusSelect({
  value,
  onChange,
}: {
  value: ApiDecisionActionStatus;
  onChange: (v: ApiDecisionActionStatus) => void;
}) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as ApiDecisionActionStatus)}
        className={`appearance-none rounded-md border bg-surface pl-2 pr-6 py-1 text-[11px] font-semibold uppercase tracking-wider focus:outline-none focus:ring-1 focus:ring-primary ${STATUS_CLASSES[value]}`}
      >
        {STATUS_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <ChevronDown className="h-3 w-3 absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground" />
    </div>
  );
}
